import {
  EngineUnavailableError,
  StaleCompileError,
  type EngineClient,
  type EngineProgress,
} from './EngineClient';
import type { FromWorker, ToWorker, WireCompileOptions } from './protocol';
import type { CompileOptions, CompileResult, EngineInfo } from './types';

interface Request {
  text: string;
  opts: CompileOptions | undefined;
  resolve: (r: CompileResult) => void;
  reject: (e: unknown) => void;
}

export function toWireOptions(opts: CompileOptions | undefined): WireCompileOptions {
  const wire: WireCompileOptions = {};
  if (opts?.lang) wire.lang = opts.lang;
  if (opts?.withStitchMap !== undefined) wire.with_stitch_map = opts.withStitchMap;
  if (opts?.maxMapLayers !== undefined) wire.max_map_layers = opts.maxMapLayers;
  return wire;
}

function defaultWorkerFactory(): Worker {
  // Literal form required so Vite bundles the worker as an ES module chunk.
  return new Worker(new URL('./engine.worker.ts', import.meta.url), { type: 'module' });
}

/**
 * Runs stitchcore in a Web Worker through the self-hosted Pyodide runtime.
 * Compiles are serialized: at most one request is in the worker, at most one waits behind it,
 * and only the latest request ever resolves.
 */
export class PyodideEngineClient implements EngineClient {
  readonly kind = 'pyodide' as const;
  private worker: Worker | null = null;
  private readyPromise: Promise<EngineInfo> | null = null;
  private readonly listeners = new Set<(p: EngineProgress) => void>();
  private lastProgress: EngineProgress | null = null;
  private nextId = 1;
  private inflight: (Request & { id: number }) | null = null;
  private pending: Request | null = null;
  private disposed = false;

  constructor(private readonly createWorker: () => Worker = defaultWorkerFactory) {}

  ready(onProgress?: (p: EngineProgress) => void): Promise<EngineInfo> {
    if (onProgress) {
      this.listeners.add(onProgress);
      if (this.lastProgress) onProgress(this.lastProgress);
    }
    if (!this.readyPromise) {
      this.readyPromise = new Promise<EngineInfo>((resolve, reject) => {
        let worker: Worker;
        try {
          worker = this.createWorker();
        } catch (err) {
          reject(new EngineUnavailableError('runtime-failed', `cannot start worker: ${String(err)}`));
          return;
        }
        this.worker = worker;
        worker.onmessage = (event: MessageEvent<FromWorker>) => {
          const msg = event.data;
          switch (msg.type) {
            case 'progress':
              this.lastProgress = msg.progress;
              this.listeners.forEach((l) => l(msg.progress));
              break;
            case 'ready':
              resolve(msg.info);
              break;
            case 'init-error':
              reject(new EngineUnavailableError(msg.reason, msg.message));
              break;
            case 'result':
            case 'error':
              this.settle(msg);
              break;
          }
        };
        worker.onerror = (event: ErrorEvent) => {
          event.preventDefault();
          reject(new EngineUnavailableError('runtime-failed', event.message || 'engine worker failed to load'));
        };
        this.send({ type: 'init' });
      });
    }
    return this.readyPromise;
  }

  compile(text: string, opts?: CompileOptions): Promise<CompileResult> {
    return new Promise<CompileResult>((resolve, reject) => {
      if (this.disposed) {
        reject(new EngineUnavailableError('runtime-failed', 'engine disposed'));
        return;
      }
      this.pending?.reject(new StaleCompileError());
      this.pending = { text, opts, resolve, reject };
      void this.pump();
    });
  }

  dispose(): void {
    this.disposed = true;
    this.pending?.reject(new StaleCompileError());
    this.inflight?.reject(new StaleCompileError());
    this.pending = null;
    this.inflight = null;
    this.worker?.terminate();
    this.worker = null;
  }

  private async pump(): Promise<void> {
    if (this.inflight || !this.pending) return;
    try {
      await this.ready();
    } catch (err) {
      const req = this.pending;
      this.pending = null;
      req?.reject(err);
      return;
    }
    if (this.inflight || !this.pending) return;
    const req = this.pending;
    this.pending = null;
    const id = this.nextId++;
    this.inflight = { ...req, id };
    this.send({ type: 'compile', id, text: req.text, options: toWireOptions(req.opts) });
  }

  private settle(msg: Extract<FromWorker, { type: 'result' | 'error' }>): void {
    const req = this.inflight;
    if (!req || req.id !== msg.id) return; // stale answer: drop it
    this.inflight = null;
    if (this.pending) {
      // A newer request is waiting: this result is already outdated.
      req.reject(new StaleCompileError());
    } else if (msg.type === 'result') {
      req.resolve(msg.result);
    } else {
      req.reject(new Error(msg.message));
    }
    void this.pump();
  }

  private send(msg: ToWorker): void {
    this.worker?.postMessage(msg);
  }
}
